/**
 * @fileoverview Orchard FROST send orchestration — ties the co-signing
 * ceremony to the chain via the PCZT endpoints.
 *
 * Pipeline (mirrors WINBIT32's shielded-transaction-client FROST path):
 *   1. POST /pczt/orchard-sighash  → sighash + per-action `alpha`s + action
 *      indices + (optional) per-action fvk.ak + an IO-finalised PCZT.
 *   2. For each Orchard action: run the two-round FROST ceremony with that
 *      action's `alpha` as the randomiser (`runOrchardInitiatorAction`).
 *   3. POST /pczt/apply-orchard-frost → inject sigs, prove, finalise.
 *   4. POST /pczt/broadcast → txid.
 *
 * Everything host-specific is injected via {@link CosignConfig} (API base,
 * network, fetch), and the WASM engine + loaded share are passed in — no
 * globals, so this slots straight into the future standalone package.
 *
 * The PCZT *build* (gathering spendable notes for the FROST UFVK) belongs to
 * the wallet/scan layer; this module starts from an already-built unsigned
 * PCZT. `buildPczt` is provided as a thin typed wrapper for callers that want
 * the server to build it from explicit spends/outputs.
 */

import type { CosignConfig } from './config';
import type { CosignTransport } from './transports';
import {
	runOrchardInitiatorAction,
	waitForCosignerJoin,
	type OrchardKeyShareForCosign,
	type FrostOrchardSetup,
	type SigningDisplay,
	type CosignCallbacks,
} from './relay';
import type { OrchardFrostWasmModule } from './orchardFrost';

export type PcztFormat = 'base64' | 'hex';

const HEX64 = /^[0-9a-fA-F]{64}$/;

// ── PCZT API client (config-injected fetch) ─────────────────────────

const postJson = async <T>(config: CosignConfig, pathSuffix: string, body: unknown): Promise<T> => {
	const resp = await config.fetchImpl(`${config.pcztApiBaseUrl}${pathSuffix}`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
	});
	const text = await resp.text();
	let parsed: any = null;
	try { parsed = text ? JSON.parse(text) : null; } catch { /* non-JSON error body */ }
	if (!resp.ok || (parsed && parsed.success === false)) {
		const msg = (parsed && typeof parsed.error === 'string') ? parsed.error : (text || `HTTP ${resp.status}`);
		throw new Error(`pczt${pathSuffix} failed: ${msg}`);
	}
	return parsed as T;
};

export interface OrchardSighashResult {
	success?: boolean;
	/** ZIP-244 Orchard sighash (64 hex / 32 bytes), shared across all actions. */
	orchardSighash: string;
	/** Number of Orchard actions that need a FROST signature. */
	orchardActionCount: number;
	/** Per-action `alpha` randomisers the PCZT pre-committed to (64 hex each). */
	orchardAlphas: string[];
	/** PCZT action index for each entry in `orchardAlphas`. */
	orchardFrostActionIndices: number[];
	/** Total Orchard actions (incl. dummies that don't need signing). */
	orchardActionTotal?: number;
	/** Per-action fvk.ak, for verifying the PCZT was built with our group key. */
	orchardFvkAks?: string[];
	/** IO-finalised PCZT to feed back into apply (dummies pre-signed). */
	pczt?: string;
	pcztFormat?: PcztFormat;
}

export const extractOrchardSighash = (
	config: CosignConfig,
	pczt: string,
	pcztFormat: PcztFormat = 'base64',
): Promise<OrchardSighashResult> =>
	postJson<OrchardSighashResult>(config, '/orchard-sighash', { pczt, pcztFormat });

export interface ApplyOrchardFrostParams {
	pczt: string;
	pcztFormat?: PcztFormat;
	orchardSignatures: string[];
	orchardFrostActionIndices?: number[];
	orchardGroupPublic?: string;
	network?: CosignConfig['network'];
}

export const applyOrchardFrostSignatures = (
	config: CosignConfig,
	params: ApplyOrchardFrostParams,
): Promise<{ pczt: string }> =>
	postJson<{ pczt: string }>(config, '/apply-orchard-frost', {
		pczt: params.pczt,
		pcztFormat: params.pcztFormat ?? 'base64',
		orchardSignatures: params.orchardSignatures,
		...(params.orchardFrostActionIndices ? { orchardFrostActionIndices: params.orchardFrostActionIndices } : {}),
		...(params.orchardGroupPublic ? { orchardGroupPublic: params.orchardGroupPublic } : {}),
		network: params.network ?? config.network,
	});

export interface BroadcastResult {
	success?: boolean;
	txid: string;
	broadcastedAt?: string;
}

export const broadcastPczt = (
	config: CosignConfig,
	signedPczt: string,
	format: PcztFormat = 'base64',
): Promise<BroadcastResult> =>
	postJson<BroadcastResult>(config, '/broadcast', { signedPczt, format });

/** Thin pass-through to /pczt/build; the caller assembles spends/outputs. */
export const buildPczt = <T = { pczt: string }>(config: CosignConfig, buildRequest: Record<string, unknown>): Promise<T> =>
	postJson<T>(config, '/build', buildRequest);

// ── Full send orchestration ──────────────────────────────────────────

export interface CosignSendParams {
	config: CosignConfig;
	wasm: OrchardFrostWasmModule;
	/** WB32COSIGN session AES key (hex). */
	encKeyHex: string;
	transport: CosignTransport;
	/** This device's Vultisig localPartyId. */
	myPartyId: string;
	/** Our loaded Orchard FROST share (from the .wult). */
	share: OrchardKeyShareForCosign;
	publicKeyPackage: { signerPubkeys: Record<string, string>; groupPublic: string };
	/** Unsigned PCZT (base64 by default) to co-sign. */
	unsignedPczt: string;
	pcztFormat?: PcztFormat;
	display: SigningDisplay;
	/**
	 * When provided, wait for a cosigner with one of these Vultisig
	 * localPartyIds to join before signing (the QR-pairing handshake).
	 * Omit if the caller already performed the join.
	 */
	validSignerIds?: string[];
	callbacks?: CosignCallbacks;
}

export interface CosignSendResult {
	txid: string;
	signedPczt: string;
	signatures: string[];
}

/**
 * Drive an unsigned PCZT through the Orchard FROST co-signing pipeline to a
 * broadcast txid. The cosigner (WINBIT32) co-signs each action over the relay.
 */
export const cosignSendPczt = async (p: CosignSendParams): Promise<CosignSendResult> => {
	const { config, wasm, transport, share, publicKeyPackage, callbacks } = p;
	const pcztFormat = p.pcztFormat ?? 'base64';
	const groupPublicHex = (publicKeyPackage.groupPublic || '').toLowerCase();

	if (p.validSignerIds && p.validSignerIds.length > 0) {
		await waitForCosignerJoin({ encKeyHex: p.encKeyHex, transport, validSignerIds: p.validSignerIds, callbacks });
	}

	callbacks?.onProgress?.('Requesting Orchard sighash from server...');
	const sh = await extractOrchardSighash(config, p.unsignedPczt, pcztFormat);

	if (!sh.orchardSighash || !HEX64.test(sh.orchardSighash)) {
		throw new Error('Server returned an invalid Orchard sighash (expected 64 hex chars).');
	}
	const actionCount = sh.orchardActionCount || 0;
	const alphas = Array.isArray(sh.orchardAlphas) ? sh.orchardAlphas : [];
	const indices = Array.isArray(sh.orchardFrostActionIndices) ? sh.orchardFrostActionIndices : [];

	if (actionCount > 0) {
		if (alphas.length !== actionCount) {
			throw new Error(
				`Server returned ${alphas.length} Orchard alphas but ${actionCount} actions need signing — ` +
				'upgrade the orchard-scanner so extract-orchard-sighash returns per-action alphas.',
			);
		}
		alphas.forEach((a, i) => {
			if (!HEX64.test(a || '')) throw new Error(`Invalid Orchard alpha at index ${i} (expected 64 hex chars).`);
		});
		assertFvkMatchesGroup(sh, actionCount, groupPublicHex, indices);
	}

	const signatures: string[] = [];
	for (let i = 0; i < actionCount; i++) {
		callbacks?.onProgress?.(`Orchard FROST signing action ${i + 1} of ${actionCount}...`);
		const frostOrchardData: FrostOrchardSetup = {
			sighash: sh.orchardSighash,
			randomizer: alphas[i],
			pubKeyPackage: { signerPubkeys: publicKeyPackage.signerPubkeys, groupPublic: publicKeyPackage.groupPublic },
		};
		const result = await runOrchardInitiatorAction({
			wasm,
			encKeyHex: p.encKeyHex,
			transport,
			myPartyId: p.myPartyId,
			orchardKeyShare: share,
			frostOrchardData,
			display: p.display,
			callbacks,
			keepAlive: i < actionCount - 1,
		});
		signatures.push(result.signature);
	}

	callbacks?.onProgress?.('Submitting signatures to server for proving...');
	const applyPczt = sh.pczt ?? p.unsignedPczt;
	const applyFormat = (sh.pczt ? sh.pcztFormat : pcztFormat) ?? 'base64';
	const applied = await applyOrchardFrostSignatures(config, {
		pczt: applyPczt,
		pcztFormat: applyFormat,
		orchardSignatures: signatures,
		orchardFrostActionIndices: indices.length === signatures.length ? indices : undefined,
		orchardGroupPublic: HEX64.test(groupPublicHex) ? groupPublicHex : undefined,
		network: config.network,
	});
	if (!applied?.pczt) throw new Error('apply-orchard-frost returned no signed PCZT.');

	callbacks?.onProgress?.('Broadcasting transaction...');
	const broadcast = await broadcastPczt(config, applied.pczt, 'base64');
	if (!broadcast?.txid) throw new Error('Broadcast returned no txid.');

	callbacks?.onProgress?.('Transaction broadcast.');
	return { txid: broadcast.txid, signedPczt: applied.pczt, signatures };
};

/**
 * Refuse to sign if any action's fvk.ak differs from our FROST group_public —
 * no FROST signature can verify against a PCZT built with a different key.
 */
const assertFvkMatchesGroup = (
	sh: OrchardSighashResult,
	actionCount: number,
	groupPublicHex: string,
	indices: number[],
): void => {
	const aks = Array.isArray(sh.orchardFvkAks) ? sh.orchardFvkAks : [];
	if (!groupPublicHex || aks.length !== actionCount) return; // nothing to verify against
	const mismatches: string[] = [];
	for (let i = 0; i < aks.length; i++) {
		const ak = (aks[i] || '').trim().toLowerCase();
		if (!HEX64.test(ak)) continue;
		if (ak !== groupPublicHex) mismatches.push(`action ${indices[i] ?? i}: ak=${ak.slice(0, 16)}…`);
	}
	if (mismatches.length > 0) {
		throw new Error(
			`Orchard FROST keys do not match the PCZT's FVK (group_public=${groupPublicHex.slice(0, 16)}…, ` +
			`but ${mismatches.join(', ')}). The PCZT was built with a different Orchard key than your share derives — ` +
			'no signature can verify until both devices share the same bundle.',
		);
	}
};
